import React, { useState, useCallback } from "react";
import { Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";

interface ContactUsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// WhatsApp icon component
const WhatsAppIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"
      fill="currentColor"
    />
  </svg>
);

const ContactUsModal: React.FC<ContactUsModalProps> = ({ isOpen, onClose }) => {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);

  const contactInfo = {
    email: "helpdesk@employability.ai",
    whatsappNumber: "+919025485168",
  };

  const whatsappTemplateMessage = "Hi EmployAbility.AI Team, I need a help.";
  const emailTemplateMessage = "Hi EmployAbility.AI Team, I need a help.";

  const handleCopyToClipboard = useCallback(
    async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedItem("email");
        toast.success(`${label} copied to clipboard`);
        setTimeout(() => setCopiedItem(null), 2000);
      } catch (err) {
        toast.error("Failed to copy to clipboard");
      }
    },
    []
  );

  const handleWhatsAppClick = useCallback(() => {
    const encodedMessage = encodeURIComponent(whatsappTemplateMessage);
    const whatsappUrl = `https://wa.me/${contactInfo.whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  }, [contactInfo.whatsappNumber, whatsappTemplateMessage]);

  const handleEmailClick = useCallback(() => {
    const encodedSubject = encodeURIComponent("Question about Employability.ai");
    const encodedBody = encodeURIComponent(emailTemplateMessage);
    const emailUrl = `mailto:${contactInfo.email}?subject=${encodedSubject}&body=${encodedBody}`;
    window.open(emailUrl, "_self");
  }, [contactInfo.email, emailTemplateMessage]);

  const handleEmailCopy = useCallback(() => {
    handleCopyToClipboard(contactInfo.email, "Email");
  }, [handleCopyToClipboard, contactInfo.email]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div 
        className="relative bg-white rounded-lg shadow-xl max-w-[420px] w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col items-start justify-between relative p-6 pb-4">
          <h2 className="text-left text-[20px] font-ubuntu font-medium text-[#001630]">
            Contact us
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Email Section */}
          <div className="flex items-start gap-3 py-4">
            {/* Email Icon Container */}
            <div className="flex h-12 w-12 items-center justify-center rounded-md border border-black/10 bg-white">
              <Mail className="h-[22px] w-[22px] text-[#001630]" />
            </div>

            {/* Email Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h4 className="text-[16px] font-ubuntu font-medium text-[#001630]">Email Id</h4>
                <button
                  className="h-[25px] w-[25px] p-0 rounded-md bg-[#F5F5F5] hover:bg-[#E8E8E8] transition-colors flex items-center justify-center"
                  onClick={handleEmailCopy}
                  aria-label="Copy Email"
                  type="button"
                >
                  {copiedItem === "email" ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4 text-[#666666]" />
                  )}
                </button>
                {copiedItem === "email" && (
                  <span className="text-sm text-green-600">Copied!</span>
                )}
              </div>
              <p 
                className="text-[14px] font-ubuntu text-[#2760EA] cursor-pointer hover:underline"
                onClick={handleEmailClick}
              >
                {contactInfo.email}
              </p>
            </div>
          </div>

          {/* Separator */}
          <div className="h-px bg-black/10 w-full mb-4"></div>

          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppClick}
            className="w-full h-12 bg-[#25D366] hover:bg-[#20BA5A] text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-colors"
            type="button"
          >
            <WhatsAppIcon />
            Send a message
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactUsModal;

